import { RcFile } from 'antd/es/upload'
import Modules from 'application/modules'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import Pagination from 'components/Pagination'
import { getLocalPaginatedList, getTotalPages } from 'components/Pagination/helpers'
import Table from 'components/Table'
import { sortLocalList } from 'components/Table/helpers'
import { HeaderConfig, SortObject } from 'components/Table/types'
import Upload from 'components/Upload'
import { UploadTypes } from 'components/Upload/types'
import AccessControlledComponent from 'contexts/ACL/ControlledComponent'
import { getACLDataFilter } from 'contexts/ACL/service'
import { I18nContext } from 'contexts/I18n/I18nProvider'
import { Roles } from 'core/auth/constants'
import { File, RTFConfigurationManagementState } from 'modules/operational/rtf/redux/configManagement/types'
import { useCallback, useContext, useEffect, useState } from 'react'

type ManagementProps = {
  configManagement: RTFConfigurationManagementState
  downloadConfigManagementFile: (file: File) => void
  deleteConfigManagementFile: (file: File) => void
  invalidFileToaster: (title: string, message: string) => void
  uploadFile: (file: RcFile) => void
}

const TABLE_CONFIG: HeaderConfig = [
  {
    label: 'logs.column_file',
    key: 'fileName',
    sortable: true,
    defaultSort: true,
    sortedby: 'DESC'
  },
]

const PER_PAGE = 10

export default function Management({ configManagement, downloadConfigManagementFile, deleteConfigManagementFile, invalidFileToaster, uploadFile }: ManagementProps) {
  const t = useContext(I18nContext)

  const [list, setList] = useState(configManagement.files)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<SortObject<File>>({
    fieldKey: TABLE_CONFIG[0].key as keyof File,
    order: TABLE_CONFIG[0].sortedby,
  })
  const [duplicateFile, setDuplicateFile] = useState(false)
  const [pendingFile, setPendingFile] = useState<RcFile | null>(null);
  const [fileToDelete, setFileToDelete] = useState<File | null>(null);

  const isSuperUser = getACLDataFilter(Modules.rtf, [], [Roles.SU]).length > 0

  useEffect(() => {
    setList(sortLocalList(configManagement.files, sort.fieldKey, sort.order))
  }, [configManagement.files])

  function OverwriteMessage() {
    return (
    <>
      <p>{t('rtf.configuration_management_file_overwrite')} <strong>{pendingFile.name}</strong></p><br/>
      <p className='overwrite-warning'>{t('rtf.configuration_management_overwrite_confirmation')}</p>
    </>)
  }

  function DeleteMessage() {
    return (
    <>
      <p>{t('rtf.configuration_management_file_delete')} <strong>{fileToDelete.fileName}</strong></p><br/>
      <p className='overwrite-warning'>{t('rtf.configuration_management_delete_confirmation')}</p>
    </>)
  }

  const onSort = (sort: SortObject<File>) => {
    setSort(sort)
    setPage(1)
    const sortedFileList = sortLocalList(configManagement.files, sort.fieldKey, sort.order)
    setList(sortedFileList)  
  }  
  
  const handleDownload = useCallback((index: number) => {
    const paginatedList = getLocalPaginatedList(list, page, PER_PAGE)
    downloadConfigManagementFile(paginatedList[index])
  }, [list, page])
 
  const handleDelete = useCallback((index: number) => {
    const paginatedList = getLocalPaginatedList(list, page, PER_PAGE)
    setFileToDelete(paginatedList[index])
  }, [list, page])

  const handleConfirmDelete = () => {
    deleteConfigManagementFile(fileToDelete)
    setFileToDelete(null)
  }
 
  const onFailedFileValidation = () => {
    invalidFileToaster(t('rtf.configuration_management'), t('upload.invalid_json_content'))
  }
  
  const handleConfirmOverwrite = async () => {
    if (pendingFile) {
      await uploadFile(pendingFile)
    }
    
    setPendingFile(null);
    setDuplicateFile(false);
  };
  
  const handleCancelOverwrite = () => {
    setPendingFile(null);
    setDuplicateFile(false);
  };
  
  const handleFileUpload = async (file: RcFile) => {
    const lastDot = file.name.lastIndexOf('.')
    if (!(lastDot === -1 ? file.name : file.name.slice(0, lastDot)).trim()) {
      return invalidFileToaster(t('rtf.configuration_management'), t('rtf.configuration_management_empty_file_name'))
    }

    const existingFile = list.find(fileEntry => fileEntry.fileName === file.name)

    if(existingFile?.isBase){
      return invalidFileToaster(t('rtf.configuration_management'), t('rtf.configuration_management_default_overwrite'))
    }

    if(existingFile) {
      setPendingFile(file)
      setDuplicateFile(true)
      return;
    }

    await uploadFile(file)
  }

  return (
    <div>
      <div className="rtf-configuration-management-buttons-container">
        <AccessControlledComponent module={Modules.rtf} role={Roles.SU}>
          <Upload type={UploadTypes.json} onConfirm={handleFileUpload} onFailedFileValidation={onFailedFileValidation}/>
        </AccessControlledComponent>
      </div>
      <ConfirmationModal isOpen={duplicateFile} onConfirm={handleConfirmOverwrite} onCancel={handleCancelOverwrite} title='rtf.configuration_management_configuration_overwrite'>
        <OverwriteMessage/>
      </ConfirmationModal>
      <ConfirmationModal isOpen={!!fileToDelete} onConfirm={handleConfirmDelete} onCancel={() => setFileToDelete(null)} title='rtf.configuration_management_configuration_delete'>
        <DeleteMessage/>
      </ConfirmationModal>
      <Table
        header={TABLE_CONFIG}
        items={getLocalPaginatedList(list, page, PER_PAGE)}
        onSortCallback={onSort}
        sort={sort}
        onSingleDownload={handleDownload}
        onDelete={isSuperUser ? handleDelete : undefined}
        canDelete={(file: File) => !file.isBase}
      />
      <div style={{marginTop: "2rem", marginBottom: '2rem'}}>
        {list.length > PER_PAGE &&
          <Pagination page={page} setPage={setPage} totalPages={getTotalPages(list.length,PER_PAGE)}></Pagination> }
      </div>
    </div>
      
  )
}
